// © Microsoft Corporation. All rights reserved.

import { AccentButton, OutlineButton } from "@microsoft/fast-components-react-msft";
import { check, addOrUpdateTelemetryObject, renderHybrid } from "@msnews/social-utils";
import React from "react";
import { SocialProps } from "../../Social.props";
import { profileManageDataPageTelemetryContracts, ProfileManageDataPageTelemetryObjects } from "./ProfileManagaDataPage.telemetry";

enum DataType {
    activity = "activity",
    comment = "comment",
    reaction = "reaction",
    follow = "follow"
}

interface ProfileManageDataPageState {
    deletingData: {
        activity: boolean;
        comment: boolean;
        reaction: boolean;
        follow: boolean;
    };
    step: number;
}

export default class ProfileManageDataPage extends React.Component<SocialProps, ProfileManageDataPageState> {
    telemetryObjects: ProfileManageDataPageTelemetryObjects;

    constructor(props) {
        super(props);
        this.state = {
            deletingData: {
                activity: true,
                comment: true,
                reaction: true,
                follow: true
            },
            step: 0
        };
    }

    toggleCheckState(stateKey: string) {
        this.setState({ deletingData: { ...this.state.deletingData, [stateKey]: !this.state.deletingData[stateKey] } });
    }

    submitClearData() {
        this.props.profileConnector.deleteMyData(this.state.deletingData);
        this.closePopup();
    }

    removeDataClickHandler(e) {
        this.setState({ step: 1});
    }

    cancelClickHandler(e) {
        this.closePopup();
    }

    closePopup() {
        this.props.popupConnector.closePopup();
    }

    render() {
        const { mainTelemetryObject, me} = this.props;
        const { step } = this.state;

        /** Generate Telemetry Objects */
        this.telemetryObjects = {
            clearData: addOrUpdateTelemetryObject(mainTelemetryObject, profileManageDataPageTelemetryContracts.clearData, { id: me.traceId }, this.telemetryObjects && this.telemetryObjects.clearData),
            clearDataConfirmation: addOrUpdateTelemetryObject(mainTelemetryObject, profileManageDataPageTelemetryContracts.clearDataConfirmation, { id: me.traceId }, this.telemetryObjects && this.telemetryObjects.clearDataConfirmation),
            step0CancelBtn: addOrUpdateTelemetryObject(mainTelemetryObject, profileManageDataPageTelemetryContracts.step0CancelBtn, { id: me.traceId }, this.telemetryObjects && this.telemetryObjects.step0CancelBtn),
            step0RemoveDataBtn: addOrUpdateTelemetryObject(mainTelemetryObject, profileManageDataPageTelemetryContracts.step0RemoveDataBtn, { id: me.traceId }, this.telemetryObjects && this.telemetryObjects.step0RemoveDataBtn),
            step1YesRemoveDataBtn: addOrUpdateTelemetryObject(mainTelemetryObject, profileManageDataPageTelemetryContracts.step1YesRemoveDataBtn, { id: me.traceId }, this.telemetryObjects && this.telemetryObjects.step1YesRemoveDataBtn),
            step1CancelDataBtn: addOrUpdateTelemetryObject(mainTelemetryObject, profileManageDataPageTelemetryContracts.step1CancelDataBtn, { id: me.traceId }, this.telemetryObjects && this.telemetryObjects.step1CancelDataBtn)

        };

        return ((step == 0 && this.step0()) || (step == 1 && this.step1()));
    }

    step0() {
        const { t, managedClasses} = this.props;
        const { activity: activityChecked, comment: commentsChecked, reaction: reactionsChecked, follow: followChecked } = this.state.deletingData;
        const uncheckedOptions = !activityChecked && !commentsChecked && !reactionsChecked && !followChecked;

        return (
            <div className={managedClasses.profileManageDataPageContainer} {...renderHybrid(this.telemetryObjects.clearData, true)}>
                <header className="cwt-header text-center">
                    <h3 className="cwt-title">{t.ProfileManageDataDeleteProfileTxt}</h3>
                    <p>{t.ProfileManageDataDeleteProfileSubTxt}</p>
                </header>
                <hr className="cwt-h-seperator"/>
                <ul className="cwt-checklist no-select">
                    <li className="cwt-checklist-item" onClick={this.toggleCheckState.bind(this, DataType.activity)}>
                        <div className="cwt-checkbox">
                            {activityChecked && <i className="cwt-icon"> {check} </i>}
                        </div>
                        <div className="cwt-checklist-item-content">
                            <span className="cwt-label">{t.ProfileManageDataDeleteMyActivityTitle}</span>
                            <p className="cwt-sub-text">{t.ProfileManageDataDeleteMyActivityTxt}</p>
                        </div>
                    </li>
                    <li className="cwt-checklist-item" onClick={this.toggleCheckState.bind(this, DataType.comment)}>
                        <div className="cwt-checkbox">
                            {commentsChecked && <i className="cwt-icon"> {check} </i>}
                        </div>
                        <div className="cwt-checklist-item-content">
                            <span className="cwt-label">{t.ProfileManageDataDeleteMyCommentsTitle}</span>
                            <p className="cwt-sub-text">
                                {t.ProfileManageDataDeleteMyCommentsTxt}
                            </p>
                        </div>
                    </li>
                    <li className="cwt-checklist-item" onClick={this.toggleCheckState.bind(this, DataType.reaction)}>
                        <div className="cwt-checkbox">
                            {reactionsChecked && <i className="cwt-icon"> {check} </i>}
                        </div>
                        <div className="cwt-checklist-item-content">
                            <span className="cwt-label">{t.ProfileManageDataDeleteMyReactionsTitle}</span>
                            <p className="cwt-sub-text">
                                {t.ProfileManageDataDeleteMyReactionsTxt}
                            </p>
                        </div>
                    </li>
                    <li className="cwt-checklist-item" onClick={this.toggleCheckState.bind(this, DataType.follow)}>
                        <div className="cwt-checkbox">
                            {followChecked && <i className="cwt-icon"> {check} </i>}
                        </div>
                        <div className="cwt-checklist-item-content">
                            <span className="cwt-label">{t.ProfileManageDataDeleteMyFollowersTitle}</span>
                            <p className="cwt-sub-text">
                                {t.ProfileManageDataDeleteMyFollowersTxt}
                            </p>
                        </div>
                    </li>
                </ul>
                <p className="cwt-sub-text text-center">{t.DeleteDataFromCommunity}</p>
                <div className="cwt-buttons">
                    <OutlineButton className="cwt-fast-button cwt-fast-button-outline" onClick={this.cancelClickHandler.bind(this)} {...renderHybrid(this.telemetryObjects.step0CancelBtn, false)}> {t.DeleteDataCancel} </OutlineButton>
                    <AccentButton disabled={uncheckedOptions} className="cwt-fast-button" onClick={this.removeDataClickHandler.bind(this)} {...renderHybrid(this.telemetryObjects.step0RemoveDataBtn, false)}> {t.DeleteDataRemoveData}</AccentButton>
                </div>
            </div>
        );
    }

    step1() {
        const {managedClasses, t} = this.props;
        return (
            <div className={managedClasses.profileManageDataPageContainer} {...renderHybrid(this.telemetryObjects.clearDataConfirmation, true)}>
                <header className="cwt-header">
                    <h3 className="cwt-title"> {t.DeleteDataConfirmationQuestion}</h3>
                </header>

                <p className="cwt-sub-text text-center"> {t.ProfileManageDataDeleteDataMsg}</p>

                <div className="cwt-buttons">
                    <OutlineButton className="cwt-fast-button" onClick={this.cancelClickHandler.bind(this)} {...renderHybrid(this.telemetryObjects.step1CancelDataBtn, false)}> {t.DeleteDataDeleteProfileCancel} </OutlineButton>
                    <AccentButton className="cwt-fast-button cwt-danger" onClick={this.submitClearData.bind(this)} {...renderHybrid(this.telemetryObjects.step1YesRemoveDataBtn, false)}> {t.DeleteDataDeleteConfirm}</AccentButton>
                </div>
            </div>
        );
    }
}